import PropTypes from 'prop-types';
import React from 'react';
import styled, { keyframes } from 'styled-components';

// Shimmer animation
const shimmer = keyframes`
  0% {
    background-position: -200% 0;
  }
  100% {
    background-position: 200% 0;
  }
`;

// Skeleton base with shimmer effect
const SkeletonBase = styled.div`
  background: ${(props) =>
    props.theme.name === 'dark'
      ? 'linear-gradient(90deg, rgba(255,255,255,0.06) 25%, rgba(255,255,255,0.1) 50%, rgba(255,255,255,0.06) 75%)'
      : 'linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%)'};
  background-size: 200% 100%;
  animation: ${shimmer} 1.5s ease-in-out infinite;
  border-radius: 4px;
`;

// Table container
const TableContainer = styled.div`
  overflow-x: auto;
  margin: -32px;
  padding: 32px;

  @media (max-width: 768px) {
    margin: 0;
    padding: 0;
    width: 100%;
    display: none;
  }
`;

const StyledTable = styled.table`
  width: 100%;
  border-collapse: collapse;
`;

const TableHeader = styled.thead`
  background: ${(props) =>
    props.theme.name === 'dark' ? 'rgba(0, 0, 0, 0.2)' : 'rgba(0, 0, 0, 0.03)'};
  border-bottom: 1px solid
    ${(props) =>
      props.theme.name === 'dark'
        ? 'rgba(255, 255, 255, 0.1)'
        : 'rgba(0, 0, 0, 0.1)'};
`;

const HeaderCell = styled.th`
  text-align: left;
  padding: 16px;
`;

const HeaderSkeleton = styled(SkeletonBase)`
  height: 12px;
  width: ${(props) => props.width || '80px'};
`;

const TableRow = styled.tr`
  border-bottom: 1px solid
    ${(props) =>
      props.theme.name === 'dark' ? 'rgba(255,255,255,0.08)' : '#e5e7eb'};

  &:last-child {
    border-bottom: none;
  }
`;

const TableCell = styled.td`
  padding: 16px;
  vertical-align: middle;
`;

const CellContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const TextSkeleton = styled(SkeletonBase)`
  height: ${(props) => props.height || '14px'};
  width: ${(props) => props.width || '100%'};
`;

const CircleSkeleton = styled(SkeletonBase)`
  width: ${(props) => props.size || '24px'};
  height: ${(props) => props.size || '24px'};
  border-radius: 50%;
  flex-shrink: 0;
`;

const CheckboxSkeleton = styled(SkeletonBase)`
  width: 18px;
  height: 18px;
  border-radius: 4px;
  flex-shrink: 0;
`;

const ActionButtonsSkeleton = styled.div`
  display: flex;
  gap: 8px;
  justify-content: flex-end;
`;

const ActionButtonSkeleton = styled(SkeletonBase)`
  width: 32px;
  height: 32px;
  border-radius: 8px;
`;

// Mobile skeleton
const MobileCardGrid = styled.div`
  display: none;

  @media (max-width: 768px) {
    display: flex;
    flex-direction: column;
    gap: 12px;
    width: 100%;
  }
`;

const MobileCard = styled.div`
  background: ${(props) =>
    props.theme.card?.background ||
    (props.theme.name === 'dark'
      ? 'rgba(255,255,255,0.05)'
      : 'rgb(248, 250, 252)')};
  border: 1px solid
    ${(props) =>
      props.theme.card?.border ||
      (props.theme.name === 'dark'
        ? 'rgba(255,255,255,0.1)'
        : 'rgb(226, 232, 240)')};
  border-radius: 12px;
  padding: 16px;
`;

const CardHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 16px;
  padding-bottom: 16px;
  border-bottom: 1px solid
    ${(props) =>
      props.theme.name === 'dark'
        ? 'rgba(255,255,255,0.08)'
        : 'rgba(0,0,0,0.08)'};
`;

const CardContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const DetailRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

/**
 * TableSkeleton - A reusable loading skeleton for tables
 *
 * @param {number} rows - Number of skeleton rows to display (default: 5)
 * @param {number} columns - Number of columns (default: 4)
 * @param {boolean} showCheckbox - Show checkbox column (default: false)
 * @param {boolean} showActions - Show action buttons column (default: true)
 * @param {number} actionsCount - Number of action buttons (default: 2)
 * @param {array} columnWidths - Array of column widths for headers (e.g., ['120px', '200px'])
 */
const TableSkeleton = ({
  rows = 5,
  columns = 4,
  showCheckbox = false,
  showActions = true,
  actionsCount = 2,
  columnWidths = [],
}) => {
  // Generate random widths for variety
  const getRandomWidth = (min, max) => {
    return `${Math.floor(Math.random() * (max - min + 1)) + min}%`;
  };

  // Generate column configurations
  const columnConfigs = Array(columns)
    .fill(null)
    .map((_, index) => ({
      headerWidth: columnWidths[index] || `${60 + Math.random() * 40}px`,
      hasSubtext: Math.random() > 0.5,
    }));

  return (
    <>
      {/* Desktop Table Skeleton */}
      <TableContainer>
        <StyledTable>
          <TableHeader>
            <tr>
              {showCheckbox && (
                <HeaderCell style={{ width: '50px' }}>
                  <CheckboxSkeleton />
                </HeaderCell>
              )}
              {columnConfigs.map((config, index) => (
                <HeaderCell key={index}>
                  <HeaderSkeleton width={config.headerWidth} />
                </HeaderCell>
              ))}
              {showActions && (
                <HeaderCell style={{ width: '120px', textAlign: 'right' }}>
                  <HeaderSkeleton width="60px" style={{ marginLeft: 'auto' }} />
                </HeaderCell>
              )}
            </tr>
          </TableHeader>
          <tbody>
            {Array(rows)
              .fill(null)
              .map((_, rowIndex) => (
                <TableRow key={rowIndex}>
                  {showCheckbox && (
                    <TableCell style={{ width: '50px' }}>
                      <CheckboxSkeleton />
                    </TableCell>
                  )}
                  {columnConfigs.map((config, colIndex) => (
                    <TableCell key={colIndex}>
                      <CellContent>
                        <TextSkeleton width={getRandomWidth(50, 90)} />
                        {config.hasSubtext && (
                          <TextSkeleton
                            width={getRandomWidth(30, 60)}
                            height="12px"
                          />
                        )}
                      </CellContent>
                    </TableCell>
                  ))}
                  {showActions && (
                    <TableCell>
                      <ActionButtonsSkeleton>
                        {Array(actionsCount)
                          .fill(null)
                          .map((_, actionIndex) => (
                            <ActionButtonSkeleton key={actionIndex} />
                          ))}
                      </ActionButtonsSkeleton>
                    </TableCell>
                  )}
                </TableRow>
              ))}
          </tbody>
        </StyledTable>
      </TableContainer>

      {/* Mobile Card Skeleton */}
      <MobileCardGrid>
        {Array(rows)
          .fill(null)
          .map((_, rowIndex) => (
            <MobileCard key={rowIndex}>
              <CardHeader>
                <CircleSkeleton size="40px" />
                <CellContent style={{ flex: 1 }}>
                  <TextSkeleton width={getRandomWidth(50, 70)} height="16px" />
                  <TextSkeleton width={getRandomWidth(30, 50)} height="12px" />
                </CellContent>
              </CardHeader>
              <CardContent>
                {Array(Math.min(columns - 1, 3))
                  .fill(null)
                  .map((_, detailIndex) => (
                    <DetailRow key={detailIndex}>
                      <TextSkeleton width="80px" height="12px" />
                      <TextSkeleton width={getRandomWidth(40, 60)} />
                    </DetailRow>
                  ))}
              </CardContent>
              {showActions && (
                <ActionButtonsSkeleton
                  style={{
                    marginTop: '16px',
                    paddingTop: '16px',
                    borderTop: '1px solid rgba(128,128,128,0.2)',
                    justifyContent: 'center',
                  }}
                >
                  {Array(actionsCount)
                    .fill(null)
                    .map((_, actionIndex) => (
                      <ActionButtonSkeleton key={actionIndex} />
                    ))}
                </ActionButtonsSkeleton>
              )}
            </MobileCard>
          ))}
      </MobileCardGrid>
    </>
  );
};

TableSkeleton.propTypes = {
  rows: PropTypes.number,
  columns: PropTypes.number,
  showCheckbox: PropTypes.bool,
  showActions: PropTypes.bool,
  actionsCount: PropTypes.number,
  columnWidths: PropTypes.arrayOf(PropTypes.string),
};

export default TableSkeleton;

// Export individual skeleton components for custom usage
export {
  SkeletonBase,
  TextSkeleton,
  CircleSkeleton,
  CheckboxSkeleton,
  ActionButtonSkeleton,
};
